import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { 
  ShoppingCart, Package, Plus, Search, Trash2, Edit, AlertTriangle,
  DollarSign, TrendingUp, Receipt, User, Minus, X, Printer,
  BookOpen, Shirt, Pencil, Dumbbell, FlaskConical, Box
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'uniform', label: 'Uniform', icon: '👔', color: 'bg-blue-500' },
  { value: 'books', label: 'Books', icon: '📚', color: 'bg-green-500' },
  { value: 'stationery', label: 'Stationery', icon: '✏️', color: 'bg-yellow-500' },
  { value: 'sports', label: 'Sports', icon: '⚽', color: 'bg-orange-500' },
  { value: 'lab', label: 'Lab Equipment', icon: '🔬', color: 'bg-purple-500' },
  { value: 'other', label: 'Other', icon: '📦', color: 'bg-gray-500' }
];

const uniformSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const pantSizes = ['26', '28', '30', '32', '34', '36', '38', '40'];
const childSizes = ['11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25'];
const allUniformSizes = [...childSizes, ...uniformSizes, ...pantSizes];

const SchoolStorePage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('pos');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [sales, setSales] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // POS State
  const [cart, setCart] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Size selection dialog
  const [sizeDialog, setSizeDialog] = useState(false);
  const [sizeSelectItem, setSizeSelectItem] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [discount, setDiscount] = useState(0);
  
  // Dialogs
  const [itemDialog, setItemDialog] = useState(false);
  const [stockDialog, setStockDialog] = useState(false);
  const [billDialog, setBillDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentBill, setCurrentBill] = useState(null);
  
  // Item Form
  const [itemForm, setItemForm] = useState({
    name: '',
    category: 'uniform',
    description: '',
    price: '',
    cost_price: '',
    sku: '',
    has_sizes: false,
    sizes: [],
    size_variants: [],  // [{size: '11', price: 100, cost_price: 80}, ...]
    is_mandatory: false,
    low_stock_threshold: 10
  });
  
  // Stock Form
  const [stockForm, setStockForm] = useState({
    item_id: '',
    size: '',
    quantity: 0,
    operation: 'add'
  });

  // Delete Sale State
  const [deleteSaleDialog, setDeleteSaleDialog] = useState(false);
  const [deletingSale, setDeletingSale] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, studentsRes, salesRes, lowStockRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/store/items`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/store/students-list`, { headers: getAuthHeader() }),  // Use store-specific students list
        axios.get(`${API_URL}/api/store/sales?page=1&page_size=50`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/store/stock/low`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/store/reports/summary?period=month`, { headers: getAuthHeader() }).catch(() => ({ data: null }))
      ]);
      
      setItems(itemsRes.data);
      setStudents(studentsRes.data || []);
      // Handle paginated sales response
      setSales(salesRes.data?.items || salesRes.data || []);
      setLowStockItems(lowStockRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Filter students for search
  const filteredStudents = students.filter(s => 
    s.name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
    s.student_id?.includes(studentSearch)
  ).slice(0, 10);

  // Cart functions
  const addToCart = (item, size = null) => {
    const existingIndex = cart.findIndex(c => c.item_id === item.id && c.size === size);
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        item_id: item.id,
        item_name: item.name,
        category: item.category,
        size: size,
        quantity: 1,
        price: item.price,
        available_stock: size ? item.stock[size] : item.stock.default
      }]);
    }
    toast.success(`${item.name} added to cart`);
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity = Math.max(1, newCart[index].quantity + delta);
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const clearCart = () => {
    setCart([]);
    setSelectedStudent(null);
    setDiscount(0);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const finalTotal = cartTotal - discount;

  // Create sale
  const createSale = async () => {
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/store/sales`,
        {
          student_id: selectedStudent.id,
          items: cart.map(c => ({
            item_id: c.item_id,
            quantity: c.quantity,
            size: c.size,
            price: c.price
          })),
          payment_mode: paymentMode,
          discount: discount
        },
        { headers: getAuthHeader() }
      );
      
      setCurrentBill(response.data);
      setBillDialog(true);
      clearCart();
      fetchData();
      toast.success('Sale completed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sale');
    }
  };

  // Item CRUD
  const saveItem = async () => {
    // Name is always required
    if (!itemForm.name) {
      toast.error('Name is required');
      return;
    }
    
    // Check price requirement based on size variants
    const hasSizeVariants = itemForm.has_sizes && itemForm.size_variants && itemForm.size_variants.length > 0;
    const hasValidSizeVariants = hasSizeVariants && itemForm.size_variants.some(sv => sv.size && sv.price);
    
    // If no size variants, base price is required
    if (!hasValidSizeVariants && !itemForm.price) {
      toast.error('Selling price is required (or add size-specific prices)');
      return;
    }
    
    try {
      // Build size_variants array for API
      const sizeVariants = itemForm.size_variants
        .filter(sv => sv.size && sv.price)
        .map(sv => ({
          size: sv.size,
          price: parseFloat(sv.price),
          cost_price: sv.cost_price ? parseFloat(sv.cost_price) : null
        }));
      
      // If size variants exist, use first variant's price as base price (for display purposes)
      let basePrice = itemForm.price ? parseFloat(itemForm.price) : 0;
      if (sizeVariants.length > 0 && !itemForm.price) {
        basePrice = sizeVariants[0].price;
      }
      
      const payload = {
        ...itemForm,
        price: basePrice,
        cost_price: itemForm.cost_price ? parseFloat(itemForm.cost_price) : null,
        low_stock_threshold: parseInt(itemForm.low_stock_threshold),
        size_variants: sizeVariants.length > 0 ? sizeVariants : null
      };
      
      if (editingItem) {
        await axios.put(`${API_URL}/api/store/items/${editingItem.id}`, payload, { headers: getAuthHeader() });
        toast.success('Item updated');
      } else {
        await axios.post(`${API_URL}/api/store/items`, payload, { headers: getAuthHeader() });
        toast.success('Item added');
      }
      
      setItemDialog(false);
      resetItemForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const deleteItem = async (id) => {
    if (!confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/store/items/${id}`, { headers: getAuthHeader() });
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetItemForm = () => {
    setItemForm({
      name: '', category: 'uniform', description: '', price: '', cost_price: '',
      sku: '', has_sizes: false, sizes: [], size_variants: [], is_mandatory: false, low_stock_threshold: 10
    });
    setEditingItem(null);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    
    // Convert size_prices back to size_variants array
    const sizeVariants = [];
    if (item.size_prices && Object.keys(item.size_prices).length > 0) {
      Object.entries(item.size_prices).forEach(([size, priceInfo]) => {
        sizeVariants.push({
          size,
          price: priceInfo.price?.toString() || '',
          cost_price: priceInfo.cost_price?.toString() || ''
        });
      });
    }
    
    setItemForm({
      name: item.name,
      category: item.category,
      description: item.description || '',
      price: item.price.toString(),
      cost_price: item.cost_price?.toString() || '',
      sku: item.sku || '',
      has_sizes: item.has_sizes,
      sizes: item.sizes || [],
      size_variants: sizeVariants,
      is_mandatory: item.is_mandatory,
      low_stock_threshold: item.low_stock_threshold
    });
    setItemDialog(true);
  };

  // Stock update
  const updateStock = async () => {
    try {
      await axios.post(`${API_URL}/api/store/stock/update`, stockForm, { headers: getAuthHeader() });
      toast.success('Stock updated');
      setStockDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update stock');
    }
  };

  const openStockDialog = (item) => {
    setStockForm({
      item_id: item.id,
      size: item.has_sizes ? item.sizes?.[0] : '',
      quantity: 0,
      operation: 'add'
    });
    setStockDialog(true);
  };

  const getCategoryInfo = (cat) => categories.find(c => c.value === cat) || categories[5];

  // Print bill function - reusable for both new sale and sales history
  const printBill = (bill) => {
    const printContent = `
      <html>
      <head>
        <title>Bill - ${bill?.bill_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 300px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
          .header h2 { margin: 0; font-size: 18px; }
          .header p { margin: 5px 0; font-size: 12px; color: #666; }
          .bill-no { font-size: 14px; font-weight: bold; margin: 10px 0; }
          .student { background: #f5f5f5; padding: 8px; border-radius: 4px; margin-bottom: 10px; font-size: 12px; }
          .items { border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 10px 0; }
          .item { display: flex; justify-content: space-between; font-size: 12px; margin: 5px 0; }
          .total { display: flex; justify-content: space-between; font-weight: bold; font-size: 16px; margin-top: 10px; }
          .payment { text-align: center; margin-top: 10px; padding: 5px; background: #e8f5e9; border-radius: 4px; font-size: 12px; }
          .pending { background: #fff3e0; }
          .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; border-top: 1px dashed #000; padding-top: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${bill?.school_name || 'School Store'}</h2>
          <p>${bill?.school_address || ''}</p>
          ${bill?.school_phone ? `<p>Phone: ${bill.school_phone}</p>` : ''}
        </div>
        <div class="bill-no">Bill No: ${bill?.bill_number}</div>
        <div style="font-size: 11px; color: #666;">${new Date(bill?.sale_date).toLocaleString()}</div>
        <div class="student">
          <strong>${bill?.student_name}</strong><br/>
          Class: ${bill?.student_class || '-'} | Roll: ${bill?.student_roll || '-'}
        </div>
        <div class="items">
          ${bill?.items?.map(item => `
            <div class="item">
              <span>${item.item_name} ${item.size ? '(' + item.size + ')' : ''} x ${item.quantity}</span>
              <span>₹${item.total}</span>
            </div>
          `).join('')}
        </div>
        ${bill?.discount > 0 ? `<div class="item"><span>Discount</span><span>-₹${bill.discount}</span></div>` : ''}
        <div class="total">
          <span>TOTAL</span>
          <span>₹${bill?.total}</span>
        </div>
        <div class="payment ${bill?.payment_status === 'pending' ? 'pending' : ''}">
          ${bill?.payment_mode === 'cash' ? '💵 Paid in Cash' : 
            bill?.payment_mode === 'online' ? '💳 Paid Online' : 
            bill?.payment_status === 'pending' ? '📝 Added to Fee (PENDING)' : '📝 Added to Fee (Paid)'}
        </div>
        <div class="footer">
          Thank you for your purchase!<br/>
          Printed on: ${new Date().toLocaleString()}
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Delete sale - check if within 2 days
  const canDeleteSale = (sale) => {
    const saleDate = new Date(sale.sale_date || sale.created_at);
    const now = new Date();
    const diffHours = (now - saleDate) / (1000 * 60 * 60);
    return diffHours <= 48;
  };

  const openDeleteDialog = (sale) => {
    setDeletingSale(sale);
    setDeleteReason('');
    setDeleteSaleDialog(true);
  };

  const executeDeleteSale = async () => {
    if (!deleteReason.trim()) {
      toast.error('Reason is mandatory');
      return;
    }
    setDeleteLoading(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/store/sales/${deletingSale.id}`,
        { 
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          data: { reason: deleteReason.trim() }
        }
      );
      toast.success(res.data.message || 'Sale deleted & stock restored');
      setDeleteSaleDialog(false);
      setDeletingSale(null);
      setDeleteReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete sale');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <DashboardLayout title="School Store">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-xl font-bold">{items.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                  <p className="text-xl font-bold">₹{summary?.total_revenue?.toLocaleString() || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                  <Receipt className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Sales</p>
                  <p className="text-xl font-bold">{summary?.total_sales || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={lowStockItems.length > 0 ? 'border-amber-500' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${lowStockItems.length > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                  <AlertTriangle className={`h-5 w-5 ${lowStockItems.length > 0 ? 'text-amber-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Low Stock</p>
                  <p className="text-xl font-bold">{lowStockItems.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>School Store Management</CardTitle>
                <CardDescription>Sell uniforms, books, and other items to students</CardDescription>
              </div>
              <Button onClick={() => { resetItemForm(); setItemDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" /> Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="pos" className="gap-2">
                  <ShoppingCart className="h-4 w-4" /> POS / Billing
                </TabsTrigger>
                <TabsTrigger value="inventory" className="gap-2">
                  <Package className="h-4 w-4" /> Inventory
                </TabsTrigger>
                <TabsTrigger value="sales" className="gap-2">
                  <Receipt className="h-4 w-4" /> Sales History
                </TabsTrigger>
              </TabsList>

              {/* POS Tab */}
              <TabsContent value="pos">
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Items Grid */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Category Filter */}
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        size="sm" 
                        variant={selectedCategory === 'all' ? 'default' : 'outline'}
                        onClick={() => setSelectedCategory('all')}
                      >
                        All
                      </Button>
                      {categories.map(cat => (
                        <Button 
                          key={cat.value}
                          size="sm" 
                          variant={selectedCategory === cat.value ? 'default' : 'outline'}
                          onClick={() => setSelectedCategory(cat.value)}
                        >
                          {cat.icon} {cat.label}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Items */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {filteredItems.filter(i => i.total_stock > 0).map(item => (
                        <Card 
                          key={item.id} 
                          className="cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => {
                            if (item.has_sizes) {
                              // Open size selection dialog
                              setSizeSelectItem(item);
                              setSizeDialog(true);
                            } else {
                              addToCart(item);
                            }
                          }}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-2xl">{getCategoryInfo(item.category).icon}</span>
                              <Badge variant="outline" className="text-xs">
                                Stock: {item.total_stock}
                              </Badge>
                            </div>
                            <p className="font-medium text-sm truncate">{item.name}</p>
                            <p className="text-lg font-bold text-green-600">₹{item.price}</p>
                            {item.has_sizes && (
                              <p className="text-xs text-muted-foreground">Multiple sizes</p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Cart */}
                  <div className="space-y-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <ShoppingCart className="h-5 w-5" /> Cart
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Student Selection */}
                        <div className="space-y-2">
                          <Label>Select Student</Label>
                          {selectedStudent ? (
                            <div className="flex items-center justify-between p-2 bg-muted rounded-lg">
                              <div>
                                <p className="font-medium">{selectedStudent.name}</p>
                                <p className="text-xs text-muted-foreground">{selectedStudent.class_name} - {selectedStudent.student_id}</p>
                              </div>
                              <Button size="sm" variant="ghost" onClick={() => setSelectedStudent(null)}>
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Input 
                                placeholder="Search student..."
                                value={studentSearch}
                                onChange={(e) => setStudentSearch(e.target.value)}
                              />
                              {studentSearch && filteredStudents.length > 0 && (
                                <div className="border rounded-lg max-h-40 overflow-y-auto">
                                  {filteredStudents.map(s => (
                                    <div 
                                      key={s.id}
                                      className="p-2 hover:bg-muted cursor-pointer text-sm"
                                      onClick={() => {
                                        setSelectedStudent(s);
                                        setStudentSearch('');
                                      }}
                                    >
                                      {s.name} - {s.class_name}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Cart Items */}
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {cart.length === 0 ? (
                            <p className="text-center text-muted-foreground py-4">Cart is empty</p>
                          ) : (
                            cart.map((item, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{item.item_name}</p>
                                  {item.size && <p className="text-xs text-muted-foreground">Size: {item.size}</p>}
                                  <p className="text-xs">₹{item.price} x {item.quantity}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => updateCartQuantity(index, -1)}>
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <span className="w-6 text-center">{item.quantity}</span>
                                  <Button size="sm" variant="ghost" onClick={() => updateCartQuantity(index, 1)}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => removeFromCart(index)}>
                                    <Trash2 className="h-3 w-3 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Totals */}
                        {cart.length > 0 && (
                          <>
                            <div className="border-t pt-4 space-y-2">
                              <div className="flex justify-between text-sm">
                                <span>Subtotal</span>
                                <span>₹{cartTotal.toLocaleString()}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-sm">Discount</Label>
                                <Input 
                                  type="number"
                                  className="w-24 h-8"
                                  value={discount}
                                  onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                                />
                              </div>
                              <div className="flex justify-between font-bold text-lg">
                                <span>Total</span>
                                <span className="text-green-600">₹{finalTotal.toLocaleString()}</span>
                              </div>
                            </div>

                            {/* Payment Mode */}
                            <div className="space-y-2">
                              <Label>Payment Mode</Label>
                              <Select value={paymentMode} onValueChange={setPaymentMode}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cash">💵 Cash</SelectItem>
                                  <SelectItem value="online">💳 Online Payment</SelectItem>
                                  <SelectItem value="add_to_fee">📝 Add to Fee</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                              <Button variant="outline" className="flex-1" onClick={clearCart}>
                                Clear
                              </Button>
                              <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={createSale}>
                                Complete Sale
                              </Button>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Inventory Tab */}
              <TabsContent value="inventory">
                <div className="space-y-4">
                  <div className="flex gap-4 items-center">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Search items..."
                        className="pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Stock</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredItems.map(item => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.name}</p>
                              {item.sku && <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getCategoryInfo(item.category).color}>
                              {getCategoryInfo(item.category).icon} {getCategoryInfo(item.category).label}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold">₹{item.price}</TableCell>
                          <TableCell>
                            {item.has_sizes ? (
                              <div className="text-xs space-y-1 max-h-24 overflow-y-auto">
                                {Object.entries(item.stock || {}).map(([size, qty]) => {
                                  const sizePrice = item.size_prices?.[size]?.price;
                                  return (
                                    <div key={size} className="flex justify-between items-center gap-2">
                                      <span className="font-medium">{size}:</span>
                                      <span className={qty <= 0 ? 'text-red-500' : ''}>{qty} pcs</span>
                                      {sizePrice && <span className="text-green-600">₹{sizePrice}</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span>{item.total_stock}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              item.stock_status === 'out_of_stock' ? 'destructive' :
                              item.stock_status === 'low_stock' ? 'warning' : 'default'
                            }>
                              {item.stock_status === 'out_of_stock' ? 'Out of Stock' :
                               item.stock_status === 'low_stock' ? 'Low Stock' : 'In Stock'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="outline" onClick={() => openStockDialog(item)}>
                                <Package className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openEditItem(item)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => deleteItem(item.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Sales History Tab */}
              <TabsContent value="sales">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bill No</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.map(sale => (
                      <TableRow key={sale.id}>
                        <TableCell><code className="bg-muted px-2 py-1 rounded">{sale.bill_number}</code></TableCell>
                        <TableCell>{new Date(sale.sale_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{sale.student_name}</p>
                            <p className="text-xs text-muted-foreground">{sale.student_class}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {sale.items?.length || 0} items
                        </TableCell>
                        <TableCell className="font-semibold">₹{sale.total?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {sale.payment_mode === 'cash' ? '💵 Cash' : 
                             sale.payment_mode === 'online' ? '💳 Online' : '📝 Add to Fee'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={sale.payment_status === 'paid' ? 'bg-green-500' : 'bg-amber-500'}>
                            {sale.payment_status === 'paid' ? 'Paid' : 'Pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => printBill(sale)}
                            data-testid={`print-sale-${sale.bill_number}`}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          {canDeleteSale(sale) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => openDeleteDialog(sale)}
                              data-testid={`delete-sale-${sale.bill_number}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input value={itemForm.name} onChange={(e) => setItemForm({...itemForm, name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm({...itemForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  Selling Price (₹) {itemForm.has_sizes && itemForm.sizes.length > 0 ? 
                    <span className="text-xs text-muted-foreground font-normal">(Optional - use size prices)</span> : 
                    <span className="text-red-500">*</span>
                  }
                </Label>
                <Input 
                  type="number" 
                  value={itemForm.price} 
                  onChange={(e) => setItemForm({...itemForm, price: e.target.value})}
                  placeholder={itemForm.has_sizes && itemForm.sizes.length > 0 ? "Optional if size prices set" : "Required"}
                />
              </div>
              <div className="space-y-2">
                <Label>Cost Price (₹)</Label>
                <Input type="number" value={itemForm.cost_price} onChange={(e) => setItemForm({...itemForm, cost_price: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SKU Code <span className="text-xs text-muted-foreground">(Auto-generated if empty)</span></Label>
                <Input 
                  value={itemForm.sku} 
                  onChange={(e) => setItemForm({...itemForm, sku: e.target.value})} 
                  placeholder="Leave empty for auto-generate"
                />
              </div>
              <div className="space-y-2">
                <Label>Low Stock Alert</Label>
                <Input type="number" value={itemForm.low_stock_threshold} onChange={(e) => setItemForm({...itemForm, low_stock_threshold: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={itemForm.description} onChange={(e) => setItemForm({...itemForm, description: e.target.value})} />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={itemForm.has_sizes} onCheckedChange={(c) => setItemForm({...itemForm, has_sizes: c})} />
              <Label>Has Size Variants</Label>
            </div>
            {itemForm.has_sizes && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Sizes</Label>
                  <p className="text-xs text-muted-foreground">Click to select sizes, then set individual prices below</p>
                  <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto p-2 border rounded-lg">
                    {allUniformSizes.map(size => (
                      <Badge 
                        key={size}
                        variant={itemForm.sizes.includes(size) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const newSizes = itemForm.sizes.includes(size) 
                            ? itemForm.sizes.filter(s => s !== size)
                            : [...itemForm.sizes, size];
                          
                          // Update size_variants when sizes change
                          let newSizeVariants = [...itemForm.size_variants];
                          if (!itemForm.sizes.includes(size)) {
                            // Adding size - add variant with base price
                            newSizeVariants.push({
                              size,
                              price: itemForm.price || '',
                              cost_price: itemForm.cost_price || ''
                            });
                          } else {
                            // Removing size - remove variant
                            newSizeVariants = newSizeVariants.filter(sv => sv.size !== size);
                          }
                          
                          setItemForm({
                            ...itemForm,
                            sizes: newSizes,
                            size_variants: newSizeVariants
                          });
                        }}
                      >
                        {size}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {itemForm.sizes.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Size-wise Pricing</Label>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          // Apply base price to all sizes
                          const newVariants = itemForm.sizes.map(size => ({
                            size,
                            price: itemForm.price || '',
                            cost_price: itemForm.cost_price || ''
                          }));
                          setItemForm({...itemForm, size_variants: newVariants});
                          toast.success('Base price applied to all sizes');
                        }}
                      >
                        Apply Base Price to All
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-20">Size</TableHead>
                            <TableHead>Sell Price (₹)</TableHead>
                            <TableHead>Cost Price (₹)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {itemForm.sizes.map(size => {
                            const variant = itemForm.size_variants.find(sv => sv.size === size) || {size, price: '', cost_price: ''};
                            return (
                              <TableRow key={size}>
                                <TableCell className="font-medium">{size}</TableCell>
                                <TableCell>
                                  <Input 
                                    type="number"
                                    className="h-8"
                                    placeholder={itemForm.price || '0'}
                                    value={variant.price}
                                    onChange={(e) => {
                                      const newVariants = itemForm.size_variants.filter(sv => sv.size !== size);
                                      newVariants.push({
                                        ...variant,
                                        size,
                                        price: e.target.value
                                      });
                                      setItemForm({...itemForm, size_variants: newVariants});
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input 
                                    type="number"
                                    className="h-8"
                                    placeholder={itemForm.cost_price || '0'}
                                    value={variant.cost_price}
                                    onChange={(e) => {
                                      const newVariants = itemForm.size_variants.filter(sv => sv.size !== size);
                                      newVariants.push({
                                        ...variant,
                                        size,
                                        cost_price: e.target.value
                                      });
                                      setItemForm({...itemForm, size_variants: newVariants});
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(false)}>Cancel</Button>
            <Button onClick={saveItem}>{editingItem ? 'Update' : 'Add'} Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock Update Dialog */}
      <Dialog open={stockDialog} onOpenChange={setStockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {items.find(i => i.id === stockForm.item_id)?.has_sizes && (
              <div className="space-y-2">
                <Label>Size</Label>
                <Select value={stockForm.size} onValueChange={(v) => setStockForm({...stockForm, size: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {items.find(i => i.id === stockForm.item_id)?.sizes?.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Operation</Label>
              <Select value={stockForm.operation} onValueChange={(v) => setStockForm({...stockForm, operation: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add Stock</SelectItem>
                  <SelectItem value="subtract">Remove Stock</SelectItem>
                  <SelectItem value="set">Set Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input type="number" value={stockForm.quantity} onChange={(e) => setStockForm({...stockForm, quantity: parseInt(e.target.value)})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDialog(false)}>Cancel</Button>
            <Button onClick={updateStock}>Update Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill Dialog */}
      <Dialog open={billDialog} onOpenChange={setBillDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Sale Completed!</DialogTitle>
          </DialogHeader>
          {currentBill && (
            <div className="space-y-4">
              <div className="text-center border-b pb-4">
                <p className="text-lg font-bold">{currentBill.school_name || 'School Store'}</p>
                {currentBill.school_address && <p className="text-xs text-muted-foreground">{currentBill.school_address}</p>}
                {currentBill.school_phone && <p className="text-xs text-muted-foreground">Ph: {currentBill.school_phone}</p>}
              </div>
              <div className="text-center">
                <p className="text-xl font-bold">{currentBill.bill_number}</p>
                <p className="text-sm text-muted-foreground">{new Date(currentBill.sale_date).toLocaleString()}</p>
              </div>
              <div className="space-y-1">
                <p><strong>Student:</strong> {currentBill.student_name}</p>
                <p><strong>Class:</strong> {currentBill.student_class}</p>
              </div>
              <div className="border rounded-lg p-3 space-y-2">
                {currentBill.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span>{item.item_name} {item.size ? `(${item.size})` : ''} x {item.quantity}</span>
                    <span>₹{item.total}</span>
                  </div>
                ))}
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-green-600">₹{currentBill.total}</span>
                </div>
              </div>
              <div className="text-center">
                <Badge className={currentBill.payment_mode === 'add_to_fee' ? 'bg-amber-500' : 'bg-green-500'}>
                  {currentBill.payment_mode === 'add_to_fee' ? 'Added to Fee' : 'Paid'}
                </Badge>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => printBill(currentBill)}>
              <Printer className="h-4 w-4 mr-2" /> Print Bill
            </Button>
            <Button onClick={() => setBillDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Size Selection Dialog */}
      <Dialog open={sizeDialog} onOpenChange={setSizeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Size</DialogTitle>
          </DialogHeader>
          {sizeSelectItem && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-2xl mb-1">{getCategoryInfo(sizeSelectItem.category).icon}</p>
                <p className="font-semibold">{sizeSelectItem.name}</p>
                <p className="text-sm text-muted-foreground">Base Price: ₹{sizeSelectItem.price}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Available Sizes</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-1">
                  {sizeSelectItem.sizes?.map(size => {
                    const stockQty = sizeSelectItem.stock?.[size] || 0;
                    const isAvailable = stockQty > 0;
                    // Get size-specific price or use base price
                    const sizePrice = sizeSelectItem.size_prices?.[size]?.price || sizeSelectItem.price;
                    return (
                      <Button
                        key={size}
                        variant={isAvailable ? "outline" : "ghost"}
                        disabled={!isAvailable}
                        className={`h-16 flex flex-col p-1 ${!isAvailable ? 'opacity-50' : 'hover:bg-green-50 hover:border-green-500'}`}
                        onClick={() => {
                          if (isAvailable) {
                            // Pass the size-specific price when adding to cart
                            addToCart({...sizeSelectItem, price: sizePrice}, size);
                            setSizeDialog(false);
                            setSizeSelectItem(null);
                          }
                        }}
                      >
                        <span className="font-bold text-sm">{size}</span>
                        <span className="text-xs text-green-600 font-semibold">₹{sizePrice}</span>
                        <span className={`text-xs ${isAvailable ? 'text-muted-foreground' : 'text-red-500'}`}>
                          {isAvailable ? `${stockQty} pcs` : 'Out'}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSizeDialog(false); setSizeSelectItem(null); }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Sale Confirmation Dialog */}
      <Dialog open={deleteSaleDialog} onOpenChange={setDeleteSaleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Delete Sale
            </DialogTitle>
          </DialogHeader>
          {deletingSale && (
            <div className="space-y-4">
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm space-y-1">
                <p><span className="text-muted-foreground">Bill:</span> <strong>{deletingSale.bill_number}</strong></p>
                <p><span className="text-muted-foreground">Student:</span> <strong>{deletingSale.student_name}</strong></p>
                <p><span className="text-muted-foreground">Amount:</span> <strong className="text-red-600">₹{deletingSale.total?.toLocaleString()}</strong></p>
                <p><span className="text-muted-foreground">Items:</span> {deletingSale.items?.map(i => `${i.item_name} x${i.quantity}`).join(', ')}</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm">
                <p className="font-medium text-green-700 dark:text-green-300">Stock will be restored:</p>
                <ul className="list-disc list-inside text-muted-foreground">
                  {deletingSale.items?.map((item, i) => (
                    <li key={i}>{item.item_name} {item.size ? `(${item.size})` : ''} — +{item.quantity} units</li>
                  ))}
                </ul>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Reason for deletion <span className="text-red-500">*</span></Label>
                <Textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g., Wrong entry, duplicate bill, customer returned..."
                  rows={3}
                  data-testid="delete-sale-reason"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setDeleteSaleDialog(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={executeDeleteSale}
                  disabled={deleteLoading || !deleteReason.trim()}
                  data-testid="confirm-delete-sale-btn"
                >
                  {deleteLoading ? 'Deleting...' : `Delete & Restore Stock`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SchoolStorePage;
